import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteShell } from "@/components/layout/SiteShell";
import { Eyebrow } from "@/components/marketing/primitives";
import { ResultPanel, type AuditResult } from "@/components/audit/AuditPage";
import { SCAN_CSS } from "@/components/audit/ScanExtras";
import { abs } from "@/content/siteConfig";

const TITLE = "Shared tracking scan result | Berk Hospitality Group";
const DESCRIPTION = "A shared result from the free conversion tracking scan.";

// Shared scan results: never indexed, canonical points at the scanner itself.
export const Route = createFileRoute("/audit_/r/$token")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: abs("/audit") }],
  }),
  component: SharedResultPage,
});

type Shared = { host: string; at: string; result: AuditResult };

function SharedResultPage() {
  const { token } = Route.useParams();
  const [state, setState] = useState<"loading" | "ok" | "missing">("loading");
  const [data, setData] = useState<Shared | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `https://bhg-windsor-proxy.jett-929.workers.dev/site/audit/r/${encodeURIComponent(token)}`,
        );
        const body = (await res.json().catch(() => null)) as (Shared & { ok?: boolean }) | null;
        if (cancelled) return;
        if (res.status !== 200 || !body || body.ok === false || !body.result) {
          setState("missing");
          return;
        }
        setData(body);
        setState("ok");
      } catch {
        if (!cancelled) setState("missing");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const date = data?.at
    ? new Date(data.at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <SiteShell>
      <style>{SCAN_CSS}</style>
      <section className="bhg-scan" style={{ padding: "48px 0 80px" }}>
        <div className="wrap" style={{ maxWidth: 920 }}>
          {state === "loading" ? (
            <div style={{ minHeight: 420 }} aria-busy="true">
            </div>
          ) : state === "missing" || !data ? (
            <div
              className="bhg-row-in"
              style={{
                background: "#ffffff",
                border: "1px solid rgba(0,47,167,0.12)",
                borderRadius: 14,
                padding: "40px 24px",
                boxShadow: "0 18px 44px -30px rgba(0,26,92,0.45)",
                textAlign: "center",
              }}
            >
              <h1 style={{ fontWeight: 300, fontSize: 26, margin: 0, color: "#002FA7" }}>
                This result has expired or the link is wrong
              </h1>
              <div style={{ marginTop: 24, maxWidth: 240, marginInline: "auto" }}>
                <Link to="/audit" className="bhg-capture-btn mono" style={{ display: "inline-block" }}>
                  Scan your site
                </Link>
              </div>
            </div>
          ) : (
            <>
              <header style={{ marginBottom: 24 }}>
                <Eyebrow>Shared result</Eyebrow>
                <h1
                  style={{
                    fontWeight: 200,
                    fontSize: "clamp(28px, 3.4vw, 42px)",
                    lineHeight: 1.12,
                    letterSpacing: "-0.025em",
                    margin: 0,
                    color: "#002FA7",
                    overflowWrap: "anywhere",
                  }}
                >
                  Tracking scan for {data.host}
                </h1>
                {date ? (
                  <p className="mono" style={{ marginTop: 10, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(0,47,167,0.55)" }}>
                    Scanned {date}
                  </p>
                ) : null}
              </header>
              <ResultPanel result={data.result} scanUrl={`https://${data.host}`} readOnly shareToken={token} />
              <div style={{ marginTop: 24 }}>
                <Link to="/audit" className="bhg-share-btn mono">
                  Run a fresh scan
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
